'use client';

import { memo, ReactNode, useEffect, useRef } from 'react';
import cn from '@/utils/classnames';
import { Env } from '@/libs/env';

export interface ThirdPartyButtonProps {
  /** 按钮文案 */
  text?: string;
  /** 按钮图标 */
  icon?: ReactNode;
  /** 点击处理方法 */
  onClick?: () => void;
  /** 用户传入的tailwind组合class */
  className?: string;
  /** 自定义调整icon的class */
  iconClassName?: string;
  /** 按钮类型 - 影响样式 */
  variant?: 'text' | 'icon-only';
  /** 按钮唯一标识 */
  id?: string;
}

/**
 * Telegram Widget 组件
 * 参考重构前的实现，使用 iframe 方式
 */
const TelegramWidget = () => {
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // 确保容器存在
    if (containerRef.current) {
      // 检查是否已经加载过脚本，避免重复加载
      if (document.querySelector(`script[data-telegram-login="${Env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME}"]`)) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', Env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME);
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-userpic', 'false');
      script.setAttribute('data-onauth', 'window.onTelegramAuth(user)');
      script.setAttribute('data-request-access', 'write');

      // 将脚本添加到当前的 DOM 元素中
      containerRef.current.appendChild(script);
    }
  }, []);

  return (
    <span
      ref={containerRef}
      className={cn(
        'absolute top-0 right-0 width-100',
        'opacity-0 overflow-hidden',
        'pointer-events-none'
      )}
    >
    </span>
  );
};

/**
 * 第三方按钮傻瓜组件
 * 只负责展示，不包含任何业务逻辑
 */
const ThirdPartyButtonComponent = ({
  text,
  icon,
  onClick,
  className,
  iconClassName,
  variant = 'text',
  id,
}: ThirdPartyButtonProps) => {
  const isTelegram = id === 'telegram';
  const isIconOnly = variant === 'icon-only';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // 基础样式
        'relative inline-flex items-center justify-center rounded-[8px] bg-surface',
        // 根据变体设置尺寸
        isIconOnly ? 'w-[32px] h-[32px]' : 'h-[32px] px-3',
        className
      )}
    >
      {icon && (
        <div
          className={cn(
            // 图标基础样式
            'flex items-center relative',
            // 根据变体设置图标尺寸
            isIconOnly ? 'w-[20px] h-[20px]' : 'w-[14px] h-[14px]',
            // 文本模式下添加右边距
            !isIconOnly && 'mr-[5px]',
            iconClassName
          )}
        >
          {icon}
        </div>
      )}

      {text && !isIconOnly && (
        <span className="text-[13px] font-medium text-tertiary text-nowrap">
          {text}
        </span>
      )}

      {/* Telegram 专用 Widget */}
      {isTelegram && <TelegramWidget />}
    </button>
  );
};

export const ThirdPartyButton = memo(ThirdPartyButtonComponent);
